// grandfathered in no-bind
/* eslint-disable react/jsx-no-bind */
import React, { useEffect, useMemo, useState } from 'react';
import { cn } from '@express-labs/raven-tools';
import { Image, Slide } from 'pure-react-carousel';
import LazyImage from '../../../LazyImage/LazyImage';
import IconV2, { IconV2IconNames, IconV2Props } from '../../../IconV2/IconV2';
import Button from '../../../DeprecatedLink/DeprecatedLink';
import VisualNavFirstCard from '../VisualNavFirstCard/VisualNavFirstCard';
import s from './RenderSlideVisualNav.scss';
import { FirstCard } from '../../Carousel';

type IconProps = {
  isSelectedFacet?: boolean;
  filterValue?: IconV2IconNames;
  filterId?: string;
  isDisabled?: boolean;
};
type RenderFirstCardProps = {
  size: number;
  deviceType: string;
  cardData: FirstCard;
};

export type RenderMediaProps = {
  filterValue: IconV2IconNames;
  handlePlayPauseClick?: (filterValue: string) => void;
  imgSrc: string;
  isLazyLoaded: boolean;
  isSelectedFacet: boolean;
  size: number;
  useVideoPlayer: boolean;
  videoRef: React.RefObject<HTMLVideoElement>;
  videoSrc: string;
  videoState?: string;
};

type VideoPlayStateIconProps = {
  videoState?: string;
};

export type RenderSlideVisualNavProps = {
  /**
   * The array index number of this slide.  This same number is used in Dot, and thumbnail buttons
   * to link them together.  For example, clicking on Dot 3 moves slide 3 into the leftmost
   * slot in the Carousel viewport.  See pure-react-carousel documentation for more info.
   * This number is generated automatically in Carousel.
   */
  i: number;

  /**
   * a unique id for the carousel. This number is used to help create unique css id's and class
   * names so that you can have more than one carousel on the page and style them differently,
   * or reference a dom element via their unique id attribute name via javascript.
   * This number is generated automatically in Carousel and passed in.
   */
  uid: number;

  /**
   * a unique categoryCarId.
   * This value is generated in raven-server.
   */
  categoryCardId?: string;

  /**
   * Description of the image being displayed in the slide
   */
  imageDescription?: string;

  /**
   * Title of the image being displayed in the slide
   */
  imageTitle?: string;

  /**
   * Card disabled state
   */
  isDisabled?: boolean;

  /**
   * Defers loading of images that are not initially visible in the Carousel
   */
  isLazyLoaded: boolean;

  /**
   * itemCount the number to display next to the image title
   */
  itemCount?: number;

  /**
   * A url to a "normal sized" image. Not a thumbnail, and not a large image.
   * This is "optional" for backwards compatibility, but as of 11/12/18 we should be using
   * this to avoid "fuzzy images" due to the browser down sampling a large image into a small
   * space.
   */
  mainImage?: string;

  mainVideo?: string;

  /**
   * Action handler for clicks on the image. Passed in by Carousel.
   */
  onClick: (i: string, ev: React.MouseEvent<HTMLElement>) => void;

  /**
   * The actual size of the device in px. Ex: 1440, 992, ..
   */
  deviceType: string;

  useVideoPlayer: boolean;

  videoState?: string;

  /**
   * Relative size of the slide, this value is represented as a percentage. Passed in by Carousel.
   * This value is determined in CarouselCategories.
   * Required to calculate the width of every slide in inline-style.
   */
  size: number;

  filterId?: string;

  filterValue: IconV2IconNames;

  isSelectedFacet?: boolean;

  handlePlayPauseClick?: (filterValue: string) => void;
};

export function RenderFirstCard(props: RenderFirstCardProps) {
  const { size, deviceType, cardData } = props;

  const slideClassName =
    deviceType === 'desktop' ? s.slideCardDesktop : s.slideCardMobile;

  const widthStyle = useMemo<React.CSSProperties>(
    () => ({ width: `${size}px` }),
    [size]
  );

  const slideStyle = useMemo(
    () => ({ width: '100%', paddingBottom: 0, height: 'auto' }),
    []
  );

  return (
    <div className={cn(s.slideCard, slideClassName)} style={widthStyle}>
      <Slide
        id="first-card"
        index={-1}
        tag="div"
        className={s.slideContainer}
        style={slideStyle}
        innerClassName={s.slideInner}
        tabIndex={-1}
      >
        <VisualNavFirstCard cardData={cardData} />
      </Slide>
    </div>
  );
}

export function RenderMedia(props: RenderMediaProps) {
  const {
    filterValue,
    handlePlayPauseClick,
    imgSrc,
    isLazyLoaded,
    isSelectedFacet,
    size,
    useVideoPlayer,
    videoRef,
    videoSrc,
    videoState,
  } = props;

  const [initialRender, setInitialRender] = useState(true);

  useEffect(() => {
    /*
      As of 8/9/2021 -
      IOS mobile web and Safari browsers does not display the first frame
      of the video as a poster image OR use the poster image attribute
      on the video tag. To solve this we will track the initial mount in
      state and show an image to the user. When the user presses the play
      button we will swap the image out for the video.
     */

    if (videoState !== 'stop' && initialRender) {
      setInitialRender(false);
    }
  }, [videoState, initialRender]);

  /* istanbul ignore next */
  return (
    <>
      {useVideoPlayer && (
        <video
          ref={videoRef}
          playsInline
          preload="metadata"
          width={`${size}px`}
          onEnded={() => handlePlayPauseClick?.(filterValue)}
          className={cn(
            s.cardVideo,
            isSelectedFacet && s.cardActive,
            initialRender && s.hideMedia
          )}
        >
          <source src={videoSrc} type="video/mp4" />
        </video>
      )}
      {isLazyLoaded ? (
        <LazyImage
          className={cn(
            isSelectedFacet && s.cardActive,
            useVideoPlayer && !initialRender && s.hideMedia
          )}
          src={imgSrc}
          alt={imgSrc}
          width={300}
        />
      ) : (
        <Image
          hasMasterSpinner={false}
          className={cn(
            isSelectedFacet && s.cardActive,
            useVideoPlayer && !initialRender && s.hideMedia
          )}
          tag="img"
          src={imgSrc}
        />
      )}
    </>
  );
}

export function VideoPlayStateIcon({ videoState }: VideoPlayStateIconProps) {
  if (videoState === 'play') {
    return <IconV2 icon="pause" size="small" className={s.playIcon} />;
  }

  return <IconV2 icon="play" size="small" className={s.playIcon} />;
}

class RenderSlideVisualNav extends React.PureComponent<RenderSlideVisualNavProps> {
  private videoRef: React.RefObject<HTMLVideoElement> = React.createRef();

  constructor(props: RenderSlideVisualNavProps) {
    super(props);
    this.handleOnClick = this.handleOnClick.bind(this);
  }

  static getIconProps(
    props: IconProps
  ): IconV2Props & { 'data-filter'?: string } {
    const { isSelectedFacet, filterValue, filterId, isDisabled } = props;
    const name: IconV2IconNames = `circle-${isSelectedFacet ? 'selected' : 'unselected'}`;
    return {
      icon: isDisabled ? 'circle-disabled' : name,
      size: 'small',
      name: filterValue,
      className: s.checkedIcon,
      'data-filter': filterId,
    };
  }

  componentDidUpdate() {
    if (!this.videoRef.current) return;

    switch (this.props.videoState) {
      case 'stop':
        this.videoRef.current.pause();
        this.videoRef.current.currentTime = 0;
        break;
      case 'play':
        this.videoRef.current.play();
        break;
      case 'pause':
        this.videoRef.current.pause();
        break;
    }
  }

  handleOnClick(ev: React.MouseEvent<HTMLElement>) {
    const { onClick, categoryCardId } = this.props;
    if (categoryCardId) {
      onClick(categoryCardId, ev);
    }
  }

  render() {
    const {
      deviceType,
      filterId,
      filterValue,
      handlePlayPauseClick,
      i,
      imageDescription,
      imageTitle,
      isDisabled,
      isLazyLoaded,
      isSelectedFacet = false,
      itemCount,
      mainImage = '',
      mainVideo = '',
      size,
      uid,
      useVideoPlayer,
      videoState,
    } = this.props;

    const slideClassName =
      deviceType === 'desktop' ? s.slideCardDesktop : s.slideCardMobile;
    const iconProps = RenderSlideVisualNav.getIconProps({
      isSelectedFacet,
      filterValue,
      filterId,
      isDisabled,
    });

    return (
      <div
        className={cn(s.slideCard, slideClassName)}
        style={{ width: `${size}px` }}
      >
        <Slide
          id={`carousel__slide--${uid}--${i}`}
          index={i}
          tag="div"
          className={s.slideContainer}
          style={{ width: '100%', paddingBottom: 0, height: 'auto' }}
          innerClassName={s.slideInner}
          tabIndex={-1}
        >
          <Button
            className={cn(
              s.wrapperLink,
              isDisabled && s.disabledBtn,
              'focus-on-modal-close'
            )}
            data-filter={filterId}
            onClick={isDisabled ? undefined : this.handleOnClick}
            tabIndex={0}
          >
            <RenderMedia
              useVideoPlayer={useVideoPlayer}
              isLazyLoaded={isLazyLoaded}
              size={size}
              videoSrc={mainVideo}
              imgSrc={mainImage}
              isSelectedFacet={isSelectedFacet}
              filterValue={filterValue}
              handlePlayPauseClick={handlePlayPauseClick}
              videoRef={this.videoRef}
              videoState={videoState}
            />
          </Button>
          <div className={s.cardBottomButtonWrapper}>
            <button
              aria-checked={isSelectedFacet}
              className={cn(s.checkButton, isDisabled && s.disabledBtn)}
              data-filter={filterId}
              disabled={isDisabled}
              id={`carousel__slide--button---${uid}--${i}`}
              name={filterValue}
              onClick={isDisabled ? undefined : this.handleOnClick}
              role="checkbox"
              type="button"
            >
              <IconV2 {...iconProps} />
              Filter products by : {filterValue}
            </button>
            {useVideoPlayer && (
              <button
                className={s.playPauseButton}
                data-state={videoState}
                data-testid="video-play-btn"
                onClick={() => handlePlayPauseClick?.(filterValue)}
                type="button"
              >
                <VideoPlayStateIcon videoState={videoState} />
              </button>
            )}
          </div>
        </Slide>
        <div className={s.imageDetails}>
          <p className={s.imageTitle}>
            {imageTitle}
            {itemCount && itemCount > 0 && (
              <span className={s.categoryCount}>(50)</span>
            )}
          </p>
          <div className={s.imageDescription}>{imageDescription}</div>
        </div>
      </div>
    );
  }
}

export default RenderSlideVisualNav;
